import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gerarRelatorioPDF } from "@/lib/report";
import { fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Printer, X, PackageMinus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HOJE = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

const PRESETS = [
  { id: "hoje", label: "Hoje", ini: HOJE, fim: HOJE },
  { id: "ontem", label: "Ontem", ini: () => addDays(-1), fim: () => addDays(-1) },
  { id: "7d", label: "Últimos 7 dias", ini: () => addDays(-6), fim: HOJE },
  { id: "mes", label: "Este mês", ini: firstOfMonth, fim: HOJE },
];

export default function RelatorioSaidas() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [preset, setPreset] = useState("7d");
  const [dataIni, setDataIni] = useState(addDays(-6));
  const [dataFim, setDataFim] = useState(HOJE());
  const [unitId, setUnitId] = useState("__todas");
  const [tipo, setTipo] = useState("__todos");
  const [categoria, setCategoria] = useState("__todas");
  const [produtoId, setProdutoId] = useState("__todos");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const scoped = useMemo(() => {
    if (user.all_units) return units;
    const ids = new Set((user.unidades || []).map((u) => u.id));
    return units.filter((u) => ids.has(u.id));
  }, [units, user]);
  const canAllUnits = user.all_units;

  useEffect(() => {
    api.get("/units").then((r) => setUnits(r.data));
    api.get("/produtos").then((r) => setProdutos(r.data));
    api.get("/categorias").then((r) => setCategorias(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { data_ini: dataIni, data_fim: dataFim };
      if (unitId !== "__todas") params.unit_id = unitId;
      if (tipo !== "__todos") params.tipo = tipo;
      if (categoria !== "__todas") params.categoria = categoria;
      if (produtoId !== "__todos") params.produto_id = produtoId;
      const { data } = await api.get("/relatorios/saidas", { params });
      setData(data);
    } finally { setLoading(false); }
  }, [dataIni, dataFim, unitId, tipo, categoria, produtoId]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p) { setDataIni(p.ini()); setDataFim(p.fim()); }
  };

  const tiposLabels = useMemo(() => data?.tipos_labels || {}, [data]);
  const inds = useMemo(() => {
    if (!data) return [];
    const arr = [{ key: "__all", label: "Total movimentado", valor: data.total }];
    Object.entries(data.por_tipo || {}).forEach(([k, v]) => arr.push({ key: k, label: tiposLabels[k] || k, valor: v }));
    return arr;
  }, [data, tiposLabels]);

  const fmtNum = (n) => (n === Math.round(n) ? n : n.toFixed(2));
  const unidadeLabel = unitId === "__todas" ? "Todas as unidades" : (scoped.find((u) => u.id === unitId)?.nome || "");

  const gerarPDF = () => {
    const token = localStorage.getItem("nexolog_token");
    const logo = data?.empresa?.logo_path ? `${BACKEND}/api/files/${data.empresa.logo_path}?auth=${token}` : null;
    const filtros = [
      { label: "Unidade", valor: unidadeLabel },
      { label: "Tipo de saída", valor: tipo === "__todos" ? "Todos" : (tiposLabels[tipo] || tipo) },
    ];
    if (categoria !== "__todas") filtros.push({ label: "Categoria", valor: categoria });
    if (produtoId !== "__todos") filtros.push({ label: "Produto", valor: produtos.find((p) => p.id === produtoId)?.nome || "" });
    gerarRelatorioPDF({
      empresa: data.empresa, logoUrl: logo, titulo: "Relatório de Saídas e Consumo",
      periodoLabel: `Período: ${dataIni.split("-").reverse().join("/")} a ${dataFim.split("-").reverse().join("/")}`,
      filtros,
      indicadores: inds.map((i) => ({ label: i.label, valor: fmtNum(i.valor) })),
      colunas: [
        { key: "dh", label: "Data/Hora", width: "16%" }, { key: "produto", label: "Produto", width: "26%" },
        { key: "unidade", label: "Unidade" }, { key: "quantidade", label: "Qtd", align: "right" },
        { key: "tipo_label", label: "Tipo de saída" }, { key: "responsavel", label: "Responsável" },
        { key: "observacao", label: "Observação" },
      ],
      linhas: (data.linhas || []).map((l) => ({ ...l, dh: fmtDate(l.created_at), quantidade: fmtNum(l.quantidade) })),
      totais: [{ key: "quantidade", valor: fmtNum(data.total) }],
      usuario: user.name, orientacao: "paisagem",
    });
  };

  const abrirDetalhe = async (saidaId) => {
    try { const { data } = await api.get(`/estoque/saidas/${saidaId}`); setDetalhe(data); } catch (_) {}
  };

  return (
    <div className="space-y-5" data-testid="relatorio-saidas-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Relatórios</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><PackageMinus className="w-7 h-7 text-primary" /> Saídas / Consumo</h1>
        </div>
        <Button onClick={gerarPDF} disabled={!data || loading} data-testid="btn-pdf-saidas"><Printer className="w-4 h-4" /> Gerar PDF / Imprimir</Button>
      </div>

      {/* Filtros */}
      <Card className="bg-card/70 p-4 space-y-3" data-testid="filtros-saidas">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => applyPreset(p.id)} data-testid={`preset-${p.id}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${preset === p.id ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{p.label}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="flex items-center gap-2"><Input type="date" value={dataIni} onChange={(e) => { setDataIni(e.target.value); setPreset(""); }} className="h-9" data-testid="filtro-data-ini" /><span className="text-muted-foreground text-xs">até</span><Input type="date" value={dataFim} onChange={(e) => { setDataFim(e.target.value); setPreset(""); }} className="h-9" data-testid="filtro-data-fim" /></div>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="h-9" data-testid="filtro-unidade"><SelectValue /></SelectTrigger>
            <SelectContent>
              {canAllUnits && <SelectItem value="__todas">Todas as unidades</SelectItem>}
              {!canAllUnits && scoped.length > 1 && <SelectItem value="__todas">Minhas unidades</SelectItem>}
              {scoped.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="h-9" data-testid="filtro-tipo"><SelectValue placeholder="Tipo de saída" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__todos">Todos os tipos</SelectItem>
              {Object.entries(tiposLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger className="h-9" data-testid="filtro-categoria"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__todas">Todas as categorias</SelectItem>
              {categorias.map((c) => <SelectItem key={c.id || c.nome} value={c.nome}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={produtoId} onValueChange={setProdutoId}>
            <SelectTrigger className="h-9" data-testid="filtro-produto"><SelectValue placeholder="Produto" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="__todos">Todos os produtos</SelectItem>
              {produtos.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Indicadores clicáveis */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="indicadores-saidas">
        {inds.map((i) => {
          const active = (i.key === "__all" && tipo === "__todos") || i.key === tipo;
          return (
            <button key={i.key} onClick={() => setTipo(i.key === "__all" ? "__todos" : i.key)} data-testid={`ind-${i.key}`}
              className={`text-left rounded-lg border p-3 transition-colors ${active ? "border-primary bg-primary/10" : "border-border bg-card/70 hover:border-primary/50"}`}>
              <div className="font-mono font-extrabold text-2xl">{loading ? "…" : fmtNum(i.valor)}</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{i.label}</div>
            </button>
          );
        })}
      </div>

      {/* Tabela */}
      <Card className="bg-card/70 p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : !data?.linhas?.length ? (
          <div className="py-16 text-center text-muted-foreground text-sm" data-testid="saidas-vazio">Nenhuma movimentação de saída para os filtros selecionados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabela-saidas">
              <thead className="bg-secondary/60 text-[10px] uppercase text-muted-foreground">
                <tr>{["Data/Hora", "Produto", "Unidade", "Qtd", "Tipo de saída", "Responsável", "Obs."].map((h) => <th key={h} className="text-left font-semibold px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.linhas.map((l, i) => (
                  <tr key={i} onClick={() => abrirDetalhe(l.saida_id)} className="hover:bg-secondary/40 cursor-pointer" data-testid={`saida-linha-${i}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-3 py-2"><div className="font-medium">{l.produto}</div><div className="text-[11px] font-mono text-muted-foreground">{l.sku}</div></td>
                    <td className="px-3 py-2">{l.unidade_codigo}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmtNum(l.quantidade)}</td>
                    <td className="px-3 py-2">{l.tipo_label}</td>
                    <td className="px-3 py-2">{l.responsavel}</td>
                    <td className="px-3 py-2 text-muted-foreground text-xs max-w-[180px] truncate">{l.observacao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!detalhe} onOpenChange={(o) => !o && setDetalhe(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Saída {detalhe?.numero}</DialogTitle></DialogHeader>
          {detalhe && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-[10px] uppercase text-muted-foreground">Unidade</div>{detalhe.unidade_nome}</div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Tipo</div>{tiposLabels[detalhe.motivo] || detalhe.motivo}</div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Responsável</div>{detalhe.usuario_nome}</div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Data</div>{fmtDate(detalhe.created_at)}</div>
              </div>
              {detalhe.observacao && <div><div className="text-[10px] uppercase text-muted-foreground">Observação</div>{detalhe.observacao}</div>}
              <div className="border border-border rounded-md divide-y divide-border mt-2">
                {detalhe.itens.map((it, i) => (
                  <div key={i} className="flex justify-between px-3 py-2"><span>{it.nome} <span className="font-mono text-muted-foreground text-xs">{it.sku}</span></span><span className="font-mono font-bold">{fmtNum(it.quantidade)}</span></div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
